import { useProductInfo } from './useProductInfo'
import '../Common.css'
import './ProductInfo.css'
import { InfoCellPair } from './InfoCellPair'
import { formatPriceInWholeCurrency } from '../../utils/formatting'
import { useLocale } from '../../hooks/useLocale'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { LithiumNamespace } from '../../utils/i18n'

function ProductInfo() {
  const { t } = useTranslation()
  const { countryCode, currency, locale } = useLocale()

  const {
    cartQuantity,
    total,
    shipping,
    totalTax,
    subtotal,
    routeIsCheckout,
    retailDeliveryFee,
    itemizedTaxes,
  } = useProductInfo(currency, countryCode)

  const shouldShowDeliveryFee: boolean =
    routeIsCheckout && retailDeliveryFee !== 0

  const formattedSubtotal = formatPriceInWholeCurrency(
    subtotal,
    currency,
    locale,
  )
  const formattedShipping = formatPriceInWholeCurrency(
    shipping,
    currency,
    locale,
  )

  const shouldShowShippingFee = shipping !== 0

  const formattedTotalTax =
    totalTax !== undefined
      ? formatPriceInWholeCurrency(totalTax, currency, locale)
      : ''
  const formattedTotal = formatPriceInWholeCurrency(total, currency, locale)

  return (
    <>
      <h6 className={'fmc-mt-6 fmc-mb-4 price-summary-label'}>
        {t('order.heading')}
      </h6>
      <div className={'price-summary-container fmc-pa-4'}>
        <h6 className={'fmc-mb-6'} data-testid={'product-amount'}>
          {t('order.subheading', { quantity: cartQuantity })}
        </h6>
        <div>
          <div
            className={
              'fds-flex fds-flex-direction--column product-info-line-items'
            }
          >
            <InfoCellPair
              testId={'product_info_subtotal'}
              title={t('order.subtotal')}
              amount={formattedSubtotal}
            />
            {shouldShowShippingFee && (
              <InfoCellPair
                testId={'product_info_shipping'}
                title={t('order.shipping')}
                amount={formattedShipping}
              />
            )}
            <TaxesAndFees
              showTotalTax={routeIsCheckout && totalTax !== undefined}
              totalTax={formattedTotalTax}
              t={t}
            />
            {shouldShowDeliveryFee && (
              <>
                <InfoCellPair
                  testId={'retail-delivery-fee'}
                  title={t('order.retailDeliveryFee')}
                  amount={formatPriceInWholeCurrency(
                    retailDeliveryFee,
                    currency,
                    locale,
                  )}
                />
                <InfoCellPair
                  testId={'card-total'}
                  title={t('order.cardTotal')}
                  amount={formattedTotal}
                />
              </>
            )}
            {itemizedTaxes.length > 0 &&
              itemizedTaxes.map((itemizedTax) => (
                <InfoCellPair
                  testId={`itemized-tax-${itemizedTax.name}`}
                  title={itemizedTax.name}
                  amount={formatPriceInWholeCurrency(
                    itemizedTax.amount,
                    currency,
                    locale,
                  )}
                  key={itemizedTax.name}
                  labelClassName={
                    'fmc-type--body2 itemized-taxes itemized-taxes-label'
                  }
                  amountClassName={'fmc-type--body2 itemized-taxes'}
                />
              ))}
          </div>
        </div>
        <div className={'fmc-mt-5 total-section'}>
          <div>
            <InfoCellPair
              testId={'product_info_total'}
              title={t('order.total')}
              amount={formattedTotal}
              tag={'h6'}
              amountClassName={'total-value'}
            />
          </div>
        </div>
      </div>
    </>
  )
}

type TaxesAndFeesProps = {
  showTotalTax: boolean
  totalTax: string
  t: TFunction<LithiumNamespace, undefined>
}
function TaxesAndFees(props: TaxesAndFeesProps) {
  const { showTotalTax, totalTax, t } = props
  const taxAmountFieldClassName = `fmc-type--body1 fds-color__text--gray3 fds-align--right ${
    showTotalTax ? 'bold-text' : ''
  }`
  const taxAmountFieldValue = showTotalTax ? totalTax : t('order.calculated')

  return (
    <InfoCellPair
      title={<>{t('order.taxes')}</>}
      testId={'product_info_tax'}
      amount={taxAmountFieldValue}
      amountClassName={taxAmountFieldClassName}
    />
  )
}

export default ProductInfo
